import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { LiveActivity } from 'capacitor-live-activity';
import { getFirestore, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { app } from '../firebaseConfig';
import { getAuth } from 'firebase/auth';

const LiveActivityContext = createContext();

export const useLiveActivity = () => {
    return useContext(LiveActivityContext);
};

export const LiveActivityProvider = ({ children }) => {
    const [activityId, setActivityId] = useState(() => localStorage.getItem('tourminator_live_activity_id'));

    useEffect(() => {
        const syncActiveActivities = async () => {
            if (Capacitor.getPlatform() !== 'ios') return;
            try {
                // Check if there are already active activities on the system
                const { items: activities } = await LiveActivity.listActivities();
                console.log('Current active Live Activities:', activities);

                const storedId = localStorage.getItem('tourminator_live_activity_id');
                const isStillActive = activities?.some(a => a.id === storedId);

                if (isStillActive) {
                    setActivityId(storedId);
                } else if (activities?.length > 0) {
                    // Find the first activity that matches our naming pattern
                    const ourActivity = activities.find(a => a.id.startsWith('tour-activity'));
                    if (ourActivity) {
                        setActivityId(ourActivity.id);
                    }
                }
            } catch (e) {
                console.error('Failed to sync activities:', e);
            }
        };
        syncActiveActivities();
    }, []);

    // Listen for Live Activity push token updates and save to Firestore
    useEffect(() => {
        if (Capacitor.getPlatform() !== 'ios') return;

        const setupPushTokenListener = async () => {
            try {
                await LiveActivity.addListener('liveActivityPushToken', async (event) => {
                    const { id, token } = event;
                    console.log(`[LiveActivity] Push token received for ${id}: ${token?.slice(0, 20)}...`);

                    // Store in Firestore for backend APNs pushes
                    const auth = getAuth(app);
                    const user = auth.currentUser;
                    if (user && token) {
                        const db = getFirestore(app);
                        await setDoc(doc(db, 'liveActivityTokens', user.uid), {
                            token,
                            activityId: id,
                            userId: user.uid,
                            updatedAt: new Date(),
                            platform: 'ios',
                        });
                        console.log('[LiveActivity] Push token saved to Firestore');
                    }
                });
            } catch (e) {
                console.error('[LiveActivity] Failed to setup push token listener:', e);
            }
        };
        setupPushTokenListener();
    }, []);

    useEffect(() => {
        if (activityId) {
            localStorage.setItem('tourminator_live_activity_id', activityId);
        } else {
            localStorage.removeItem('tourminator_live_activity_id');
        }
    }, [activityId]);

    const startActivity = useCallback(async (data) => {
        if (Capacitor.getPlatform() !== 'ios') return;

        try {
            console.log('Starting Live Activity with push support:', data);

            // Use a stable ID to avoid duplicates
            const activityInstanceId = `tour-activity-primary`;

            const options = {
                name: "TourActivityWidget",
                id: activityInstanceId,
                attributes: {
                    tourName: String(data.tourName || ""),
                    eventDate: String(data.eventDate || ""),
                    totalActivities: String(data.totalActivities || "0")
                },
                contentState: {
                    currentActivity: String(data.currentActivity || ""),
                    activityTime: String(data.activityTime || ""),
                    activityTimeEnd: data.activityTimeEnd ? String(data.activityTimeEnd) : "",
                    nextActivity: String(data.nextActivity || ""),
                    nextActivityTime: String(data.nextActivityTime || ""),
                    tourProgress: String(Math.round((data.tourProgress || 0) * 100)),
                    activityType: String(data.activityType || "general"),
                    venue: String(data.venue || ""),
                    location: String(data.location || ""),
                    currentActivityStart: String(data.currentActivityStart || ""),
                    currentActivityEnd: String(data.currentActivityEnd || ""),
                    lastUpdated: new Date().toISOString()
                }
            };

            // Cleanup any other orphaned tour activities on the system before starting new one
            try {
                const { items } = await LiveActivity.listActivities();
                for (const act of (items || [])) {
                    if (act.id !== activityInstanceId) {
                        await LiveActivity.endActivity({ id: act.id, contentState: {} });
                    }
                }
            } catch (e) { /* silent */ }

            // Use startActivityWithPush to enable APNs-based background updates
            console.log('Calling native startActivityWithPush with options:', JSON.stringify(options));
            await LiveActivity.startActivityWithPush(options);

            console.log('Live Activity started with push support, ID:', activityInstanceId);
            setActivityId(activityInstanceId);
            return activityInstanceId;
        } catch (error) {
            console.error('Error starting Live Activity:', error);
        }
    }, []);

    const updateActivity = useCallback(async (data) => {
        if (Capacitor.getPlatform() !== 'ios' || !activityId) return;

        try {
            await LiveActivity.updateActivity({
                id: activityId,
                contentState: {
                    currentActivity: String(data.currentActivity || ""),
                    activityTime: String(data.activityTime || ""),
                    activityTimeEnd: data.activityTimeEnd ? String(data.activityTimeEnd) : "",
                    nextActivity: String(data.nextActivity || ""),
                    nextActivityTime: String(data.nextActivityTime || ""),
                    tourProgress: String(Math.round((data.tourProgress || 0) * 100)),
                    activityType: String(data.activityType || "general"),
                    venue: String(data.venue || ""),
                    location: String(data.location || ""),
                    currentActivityStart: String(data.currentActivityStart || ""),
                    currentActivityEnd: String(data.currentActivityEnd || ""),
                    lastUpdated: new Date().toISOString()
                }
            });
            console.log('Live Activity updated');
        } catch (error) {
            console.error('Error updating Live Activity, assumes dead:', error);
            setActivityId(null); // Clear dead ID
            throw error; // Let caller know
        }
    }, [activityId]);

    const endActivity = useCallback(async () => {
        if (Capacitor.getPlatform() !== 'ios' || !activityId) return;

        try {
            await LiveActivity.endActivity({
                id: activityId,
                contentState: {
                    currentActivity: 'Tour Completed',
                    activityTime: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
                    nextActivity: '',
                    nextActivityTime: '',
                    tourProgress: '100',
                    activityType: 'off',
                    venue: '',
                    location: '',
                    lastUpdated: new Date().toISOString()
                }
            });

            // Clean up the push token from Firestore
            try {
                const auth = getAuth(app);
                const user = auth.currentUser;
                if (user) {
                    const db = getFirestore(app);
                    await deleteDoc(doc(db, 'liveActivityTokens', user.uid));
                    console.log('[LiveActivity] Push token cleaned up from Firestore');
                }
            } catch (e) { /* silent cleanup */ }

            setActivityId(null);
            console.log('Live Activity ended');
        } catch (error) {
            console.error('Error ending Live Activity:', error);
        }
    }, [activityId]);

    const value = React.useMemo(() => ({
        startActivity,
        updateActivity,
        endActivity,
        isActive: !!activityId,
        activityId
    }), [startActivity, updateActivity, endActivity, activityId]);

    return (
        <LiveActivityContext.Provider value={value}>
            {children}
        </LiveActivityContext.Provider>
    );
};
